import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useTaskRecovery } from 'af-root/hooks/useTaskRecovery';

import type { W_PreviousFieldReport_FindAllForWorkOrder_VM_Row } from 'ab-api/web/fieldReportTypePDFWorkOrderLookup/findPreviousFieldReportsForWorkOrder';

import type FieldReportTypePreviewInModalVM from 'ab-viewModels/fieldReport/fieldReportTypeSidePanel.viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import DeleteCell from 'af-fields/SimpleTable/DeleteCell';

import SimpleTable from 'af-components/Controls/SimpleTable';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import LabelWithColor from 'af-components/LabelWithColor';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import DateCell from 'af-components/Table6/Cells/DateCell';

import * as FieldReportActions from 'af-actions/fieldReport';
import * as WorkOrderActions from 'af-actions/workOrder';

import styles from './styles.module.scss';

const ALLOWED_RETRIES = 4;

interface OwnProps {
	workOrderId: number;
	onPreviewPFRT: (fetchedFieldReportType: FieldReportTypePreviewInModalVM, headerValue: string) => void;
	actionsEnabled: boolean;
	showSegmentLabel?: boolean;
	canDeletePFRT: boolean;
	onDeletePFRT?: (row: W_PreviousFieldReport_FindAllForWorkOrder_VM_Row) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const PreviousFieldReportsSection: React.FC<Props> = (props) => {

	const {
		workOrderId,
		onPreviewPFRT,
		findPreviousFieldReports,
		findOrCreateGroup,
		downloadFieldReportTypePDF,
		findFieldReportTypeForPreview,
		onDeletePFRT,
		actionsEnabled,
		showSegmentLabel = true,
		canDeletePFRT,
	} = props;

	const [previousFieldReports, setPreviousFieldReports] = React.useState<Nullable<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row[]>>(null);

	const taskRecovery = useTaskRecovery();

	const fetchData = React.useCallback(async () => {
		const fetchedData = await findPreviousFieldReports(workOrderId);
		setPreviousFieldReports(fetchedData.previousFieldReports);
	}, [findPreviousFieldReports, workOrderId]);

	React.useEffect(() => {
		fetchData();
	}, [fetchData, workOrderId]);

	const onExportAsPDFClick = React.useCallback((fieldReportTypeIds: number[], isForCustomer: boolean, reportName: string) => async () => {
		const groupCode = await findOrCreateGroup(fieldReportTypeIds, isForCustomer);
		if (!!groupCode) {
			await downloadFieldReportTypePDF(groupCode, reportName, taskRecovery, ALLOWED_RETRIES, isForCustomer);
		}
	}, [downloadFieldReportTypePDF, findOrCreateGroup, taskRecovery]);

	const onPreviewClick = React.useCallback((row: W_PreviousFieldReport_FindAllForWorkOrder_VM_Row) => async () => {
		const fetchedFieldReportType = await findFieldReportTypeForPreview(row.fieldReportId, row.fieldReportTypeId);
		onPreviewPFRT(fetchedFieldReportType, `${row.reportTypeName} ${row.fieldReportCode}`);
	}, [findFieldReportTypeForPreview, onPreviewPFRT]);

	const onDeleteClick = React.useCallback((row: W_PreviousFieldReport_FindAllForWorkOrder_VM_Row) => async () => {
		if (canDeletePFRT && onDeletePFRT) {
			onDeletePFRT(row);
		}
	}, [canDeletePFRT, onDeletePFRT]);

	const renderDownload = React.useCallback((row: W_PreviousFieldReport_FindAllForWorkOrder_VM_Row) => {
		return (
			<span className={styles['download-icon']}>
				<span className="icon-download" onClick={onExportAsPDFClick([row.fieldReportTypeId], false, row.reportTypeName)} />
			</span>
		);
	}, [onExportAsPDFClick]);

	const renderPreview = React.useCallback((row: W_PreviousFieldReport_FindAllForWorkOrder_VM_Row) => {
		return (
			<span className={styles['preview-icon']}>
				<span className="icon-search pointer" onClick={onPreviewClick(row)} />
			</span>
		);

	}, [onPreviewClick]);

	const columns: SimpleTableProps<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row>['columns'] = React.useMemo(() => {
		const _columns: SimpleTableProps<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row>['columns'] = [
			{
				id: 'reportTypeName',
				accessor: 'reportTypeName',
				header: 'Report Type',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, string>) => {
					return _cell.getValue() ?? <EmptyCell />;
				},
				enableSorting: true,
			},
			{
				id: 'dueDate',
				accessor: 'dueDate',
				header: 'Date of Work',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, string>) => {
					return TimeUtils.formatDate(_cell.getValue(), TimeFormat.DATE_ONLY_SLASHES) ?? <EmptyCell />;
				},
				enableSorting: true,
			},
			{
				id: 'fieldReportCode',
				accessor: 'fieldReportCode',
				header: 'Field Report',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, string>) => {
					return _cell.getValue() ?? <EmptyCell />;
				},
				enableSorting: true,
			},
			{
				id: 'crewType',
				accessor: 'crewType',
				header: 'Crew Type',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, Date>) => {
					return _cell.row.original.crewType ?
						<LabelWithColor color={_cell.row.original.crewType.color} text={_cell.row.original.crewType.name} />
						: <EmptyCell />;
				},
				enableSorting: true,
			},
			{
				id: 'download',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, string>) => {
					return actionsEnabled ? renderDownload(_cell.row.original) : <EmptyCell />;
				},
				header: 'Download',
				accessor: 'download',
			},
			{
				id: 'preview',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, string>) => {
					return actionsEnabled ? renderPreview(_cell.row.original) : <EmptyCell />;
				},
				header: 'Preview',
				accessor: 'preview',
			},
			{
				id: 'updatedAt',
				accessor: 'updatedAt',
				header: 'Updated At',
				cell: (_cell: CellContext<W_PreviousFieldReport_FindAllForWorkOrder_VM_Row, Date>) => {
					return (

						_cell.row.original.updatedAt
							? (
								<DateCell
									date={_cell.row.original.updatedAt}
									dateSourceFormat={TimeFormat.ISO_DATETIME}
									details={_cell.row.original.updatedBy}
									format={TimeFormat.DATE_ONLY}
									isCustomerService={!!_cell.row.original.updatedAt && !_cell.row.original.updatedBy}
									isLeftAligned={true}
									withTime={true}
								/>
							)
							: <EmptyCell message="N/A" />
					);
				},
				enableSorting: true,
			},
		];
		if (actionsEnabled && canDeletePFRT) {
			_columns.push({
				id: 'delete',
				isDisplayColumn: true,
				header: null,
				cell: (_cell) => <DeleteCell onDeleteClick={onDeleteClick(_cell.row.original)} />,
				size: 16,
			});
		}
		return _columns;
	}, [actionsEnabled, canDeletePFRT, onDeleteClick, renderDownload, renderPreview]);

	return (
		<div className="work-order-info-modal__previous-report-types-container m-t-m">
			<SimpleTable
				columns={columns}
				label={!!showSegmentLabel ? 'Previous Report Types' : undefined}
				labelTooltip={!!showSegmentLabel ? 'Previous Field Report Types attached to this Work Order to provide context for what was done before.' : undefined}
				rows={previousFieldReports ?? []}
			/>
		</div>
	);

};

function mapDispatchToProps() {
	return {
		findPreviousFieldReports: WorkOrderActions.findPreviousFieldReports,
		findFieldReportTypeForPreview: FieldReportActions.findFieldReportTypeForPreview,
		findOrCreateGroup: FieldReportActions.findOrCreateGroup,
		downloadFieldReportTypePDF: FieldReportActions.downloadFieldReportTypePDF,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(connector);

export default enhance(PreviousFieldReportsSection);
